import { useState, useRef, useCallback, useEffect } from 'react'
import { streamChat } from '@/services/ai-service'
import { checkContent } from '@/utils/content-filter'
import { createStreamRenderer } from '@/utils/stream-renderer'
import { AGENT1_SYSTEM_PROMPT, AGENT2_SYSTEM_PROMPT, AGENT3_SYSTEM_PROMPT } from '@/constants/system-prompt'
import type { ErrorType, AgentState, AgentStep } from '#/index'

// ==================== 常量 ====================
export const MIN_CHARS = 10
export const MAX_CHARS = 100
const MAX_RETRY_LIMIT = 3
const BACKOFF_DELAYS = [3, 5, 10]
/** Agent 1 最大输出 token 数 — 正常分析够用，也防极端输出 */
const AGENT1_MAX_TOKENS = 2000
/** 流式渲染默认速度（字符/秒） */
const DEFAULT_CHARS_PER_SECOND = 100
/** 切回前台后追赶速度（字符/秒） */
const CATCHUP_CHARS_PER_SECOND = 300
/** 追赶结束后恢复的常速（字符/秒） */
const NORMAL_CHARS_PER_SECOND = 100
/** 追赶持续时间（ms） */
const CATCHUP_DURATION_MS = 3000

// 格式为 "title\ndesc"，与 ErrorState 组件的 split('\n') 解析耦合
const NEED_CLARIFY_MESSAGE = '需求描述不够具体\n请补充更多信息，例如：为后台管理系统增加一个角色权限管理功能'

const REJECTED_MESSAGE = '输入内容不符合安全规范\n请修正后重试'

/** Agent 1 特殊拦截标记 → 用户提示文案 + 流水线终止结果 */
const SPECIAL_TOKENS: Record<string, { content: string; result: AgentRunResult }> = {
  INVALID: { content: NEED_CLARIFY_MESSAGE, result: 'need_clarify' },
  REJECTED: { content: REJECTED_MESSAGE, result: 'rejected' },
}

/** runAgent 返回状态 */
type AgentRunResult = 'success' | 'error' | 'need_clarify' | 'rejected'

/** 三个 Agent 的 System Prompt */
const AGENT_PROMPTS = [AGENT1_SYSTEM_PROMPT, AGENT2_SYSTEM_PROMPT, AGENT3_SYSTEM_PROMPT]

/** Agent 的 user message 构建函数：Agent 2/3 接收上一环节输出 */
function buildUserMessage(step: number, originalInput: string, agents: AgentState[]): string {
  if (step === 0) {
    return originalInput
  }
  if (step === 1) {
    return `请根据以下需求分析报告生成PRD：\n\n${agents[0].content}`
  }
  // step === 2：基于 PRD 文档生成原型设计建议
  return `请根据以下PRD输出原型设计建议：\n\n${agents[1].content}`
}

// ==================== Hook 接口 ====================
interface UseStreamGenerationReturn {
  agents: AgentState[]
  activeViewStep: AgentStep
  setActiveViewStep: (step: AgentStep) => void
  onUserStepClick: (step: AgentStep) => void
  isGenerating: boolean
  startGeneration: (input: string) => Promise<void>
  retry: () => void
  clearError: () => void
}

const EMPTY_AGENTS = (): AgentState[] => [
  { content: '', status: 'pending', error: null },
  { content: '', status: 'pending', error: null },
  { content: '', status: 'pending', error: null },
]

// ==================== Hook 实现 ====================
export function useStreamGeneration(): UseStreamGenerationReturn {
  const [agents, setAgents] = useState<AgentState[]>(EMPTY_AGENTS)
  const [activeViewStep, setActiveViewStep] = useState<AgentStep>(0)
  const [isGenerating, setIsGenerating] = useState(false)

  // ---- Refs ----
  const agentsRef = useRef<AgentState[]>(EMPTY_AGENTS())
  const lastInputRef = useRef('')
  const retryCountRef = useRef(0)
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const userNavigatedRef = useRef(false)
  /** 当前活跃的流式渲染器（用户切步时 destroy 用） */
  const activeRendererRef = useRef<ReturnType<typeof createStreamRenderer> | null>(null)
  /** 页面是否隐藏（切后台时为 true，后续 Agent 不限速全速执行） */
  const isPageHiddenRef = useRef(false)

  // 同步 agents 到 ref（用于回调中读取最新值）
  useEffect(() => {
    agentsRef.current = agents
  }, [agents])

  // ---- 清理定时器 ----
  const clearTimer = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current)
      countdownTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => clearTimer()
  }, [clearTimer])

  /** 销毁活跃渲染器并清空引用（INVALID 路径勿用：需先 null 再 destroy 防 onComplete 覆盖） */
  const destroyActiveRenderer = useCallback(() => {
    activeRendererRef.current?.destroy()
    activeRendererRef.current = null
  }, [])

  // ---- 页面可见性：切后台快进，切回 3x 速追赶后恢复常速 ----
  useEffect(() => {
    let restoreTimer: ReturnType<typeof setTimeout> | null = null

    const handle = () => {
      const renderer = activeRendererRef.current
      if (document.hidden) {
        isPageHiddenRef.current = true
        renderer?.setSkipMode(true)
        // 切后台时取消前次恢复定时器，避免切回后误改速度
        if (restoreTimer) {
          clearTimeout(restoreTimer)
          restoreTimer = null
        }
      } else {
        isPageHiddenRef.current = false
        if (renderer) {
          renderer.setSkipMode(false)
          renderer.setCharsPerSecond(CATCHUP_CHARS_PER_SECOND)
          if (restoreTimer) clearTimeout(restoreTimer)
          restoreTimer = setTimeout(() => {
            restoreTimer = null
            if (activeRendererRef.current === renderer) {
              renderer.setCharsPerSecond(NORMAL_CHARS_PER_SECOND)
            }
          }, CATCHUP_DURATION_MS)
        }
      }
    }
    document.addEventListener('visibilitychange', handle)
    return () => {
      document.removeEventListener('visibilitychange', handle)
      if (restoreTimer) clearTimeout(restoreTimer)
    }
  }, [])

  // ---- 更新单个 Agent 状态 ----
  const updateAgent = useCallback((index: number, update: Partial<AgentState>) => {
    setAgents((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], ...update }
      return next
    })
  }, [])

  // ---- 清除错误 ----
  const clearError = useCallback(() => {
    clearTimer()
    setAgents((prev) => prev.map((a) => (a.status === 'error' ? { ...a, error: null, status: 'pending' as const } : a)))
  }, [clearTimer])

  // ---- 执行单个 Agent 的流式生成 ----
  const runAgent = useCallback(
    async (stepIndex: number, originalInput: string): Promise<AgentRunResult> => {
      const systemPrompt = AGENT_PROMPTS[stepIndex]
      const userMessage = buildUserMessage(stepIndex, originalInput, agentsRef.current)

      updateAgent(stepIndex, { status: 'running', error: null, content: '' })

      if (!userNavigatedRef.current) {
        setActiveViewStep(stepIndex as AgentStep)
      }

      const streamOptions = stepIndex === 0 ? { maxTokens: AGENT1_MAX_TOKENS } : undefined

      try {
        const stream = streamChat(systemPrompt, userMessage, streamOptions)
        let rawContent = ''

        // onComplete 触发时 resolve 此 Promise，runAgent 借此等待渲染结束
        let resolveRender: () => void
        const renderComplete = new Promise<void>((resolve) => {
          resolveRender = resolve
        })

        const renderer = createStreamRenderer(
          (text) => updateAgent(stepIndex, { content: text }),
          () => {
            if (activeRendererRef.current === renderer) {
              updateAgent(stepIndex, { status: 'done' })
            }
            resolveRender()
          },
          // 切后台后新 Agent 以极大速度全量渲染（renderer 内 >=99999 走快速通道）
          // 直接读 document.hidden 而非 isPageHiddenRef，避免异步缺口中的时序竞态
          document.hidden ? 99999 : DEFAULT_CHARS_PER_SECOND
        )
        activeRendererRef.current = renderer

        for await (const chunk of stream) {
          rawContent += chunk
          renderer.push(chunk)

          // Agent 1：持续检测拦截标记，命中立即 break 节省后续渲染
          if (stepIndex === 0) {
            const trimmed = rawContent.trim()
            if (trimmed === 'INVALID' || trimmed === 'REJECTED') break
          }
        }
        renderer.end()

        const matched = stepIndex === 0 && SPECIAL_TOKENS[rawContent.trim()]
        if (matched) {
          // 先断联再 destroy：防止 onComplete 设 status 为 done
          activeRendererRef.current = null
          renderer.destroy()
          updateAgent(stepIndex, {
            status: 'error',
            content: matched.content,
            error: {
              type: matched.result === 'need_clarify' ? 'need_clarify' : 'rejected',
              message: matched.content,
              retryCount: 0,
            },
          })
          retryCountRef.current = 0
          return matched.result
        }

        // 等待渲染动画播完，后续 Agent 才会启动
        await renderComplete
        activeRendererRef.current = null
        retryCountRef.current = 0
        return 'success'
      } catch (err: unknown) {
        destroyActiveRenderer()

        const errorMessage = err instanceof Error ? err.message : ''
        const isServiceBusy = errorMessage.includes('503') || errorMessage.includes('429')

        const errorType: ErrorType = isServiceBusy ? 'service-busy' : 'network'
        const title = isServiceBusy ? '当前服务繁忙，请稍后重试' : '生成失败，请稍后重试'
        const desc = isServiceBusy ? 'AI 服务暂时过载，稍等片刻后自动重试' : '请检查网络连接或确认 API Key 是否有效'

        updateAgent(stepIndex, {
          status: 'error',
          error: {
            type: errorType,
            message: `${title}\n${desc}`,
            retryCount: retryCountRef.current,
          },
        })
        return 'error'
      }
    },
    [updateAgent, destroyActiveRenderer]
  )

  // ---- 带退避重试的 Agent 运行 ----
  const retryRunnerRef = useRef<(stepIndex: number, originalInput: string) => Promise<AgentRunResult>>(
    async () => 'error'
  )

  const runAgentWithRetry = useCallback(
    async (stepIndex: number, originalInput: string): Promise<AgentRunResult> => {
      const result = await runAgent(stepIndex, originalInput)
      if (result !== 'error') return result

      const agentState = agentsRef.current[stepIndex]
      if (agentState.error?.type === 'service-busy' && retryCountRef.current < MAX_RETRY_LIMIT) {
        return new Promise<AgentRunResult>((resolve) => {
          const delaySeconds = BACKOFF_DELAYS[retryCountRef.current] || 10
          let countdown = delaySeconds

          updateAgent(stepIndex, {
            status: 'error',
            error: {
              ...agentState.error!,
              retryCount: retryCountRef.current,
              countdown,
            },
          })

          countdownTimerRef.current = setInterval(() => {
            countdown--
            if (countdown <= 0) {
              clearTimer()
              retryCountRef.current += 1
              retryRunnerRef.current(stepIndex, originalInput).then(resolve)
            } else {
              updateAgent(stepIndex, {
                status: 'error',
                error: {
                  ...agentState.error!,
                  retryCount: retryCountRef.current,
                  countdown,
                },
              })
            }
          }, 1000)
        })
      }

      return 'error'
    },
    [runAgent, updateAgent, clearTimer]
  )

  // 同步 ref 到最新 runAgentWithRetry，供 setInterval 回调使用
  useEffect(() => {
    retryRunnerRef.current = runAgentWithRetry
  }, [runAgentWithRetry])

  // ---- 用户手动切换步骤（纯视图切换，不干预渲染器） ----
  const onUserStepClick = useCallback((step: AgentStep) => {
    userNavigatedRef.current = true
    setActiveViewStep(step)
  }, [])

  // ---- 从指定 Agent 开始串联执行 ----
  const runPipelineFrom = useCallback(
    async (agentIndex: number, originalInput: string) => {
      setIsGenerating(true)
      for (let step = agentIndex; step < 3; step++) {
        const result = await runAgentWithRetry(step, originalInput)
        if (result !== 'success') break
      }
      setIsGenerating(false)
    },
    [runAgentWithRetry]
  )

  // ---- 开始生成 ----
  const startGeneration = useCallback(
    async (input: string) => {
      if (isGenerating) return
      if (input.length < MIN_CHARS || input.length > MAX_CHARS) return

      clearTimer()
      retryCountRef.current = 0
      userNavigatedRef.current = false
      isPageHiddenRef.current = false
      destroyActiveRenderer()

      // 内容审核
      const checkResult = checkContent(input)
      if (!checkResult.passed) {
        setAgents((prev) =>
          prev.map((a, i) =>
            i === 0
              ? {
                  content: '',
                  status: 'error' as const,
                  error: {
                    type: 'content-review',
                    message: '内容审核不通过，请修正后重试\n您的输入包含不符合规定的内容，请修改后重新输入',
                    retryCount: 0,
                  },
                }
              : { ...a, status: 'pending' as const }
          )
        )
        setActiveViewStep(0)
        return
      }

      lastInputRef.current = input

      // 重置所有 Agent 状态
      setAgents(EMPTY_AGENTS())
      setActiveViewStep(0)

      await runPipelineFrom(0, input)
    },
    [isGenerating, clearTimer, runPipelineFrom, destroyActiveRenderer]
  )

  // ---- 手动重试 ----
  const retry = useCallback(() => {
    if (isGenerating) return
    clearTimer()

    const currentAgents = agentsRef.current
    const errorAgentIndex = currentAgents.findIndex((a) => a.status === 'error')
    if (errorAgentIndex === -1) return

    const originalInput = lastInputRef.current
    if (!originalInput) return

    destroyActiveRenderer()
    isPageHiddenRef.current = false
    retryCountRef.current += 1

    // 重置该 Agent 及之后的状态
    setAgents((prev) =>
      prev.map((a, i) => (i >= errorAgentIndex ? { content: '', status: 'pending' as const, error: null } : a))
    )

    runPipelineFrom(errorAgentIndex, originalInput)
  }, [isGenerating, clearTimer, runPipelineFrom, destroyActiveRenderer])

  return {
    agents,
    activeViewStep,
    setActiveViewStep,
    onUserStepClick,
    isGenerating,
    startGeneration,
    retry,
    clearError,
  }
}
